import { CheckCircleOutlined } from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, ConfigProvider, Flex, Popconfirm, Space, Tag, Tooltip, Typography } from "antd";
import { ticketActions } from "../../features/ticket/ticketSlice";
import { type WOAction, WO_STATUS_TRANSITIONS } from "../constants/woStatusTransitions";
import styles from "./WOActionButtons.module.css";

type WOActionButtonsProps = {
  ticketId: string;
  currentStatus: string;
  role: "management" | "field";
  dispatch: Dispatch;
  onOpenDelayModal: () => void;
  onOpenRejectModal?: () => void;
  onOpenSummaryModal?: () => void;
  allTasksDone?: boolean;
  blockedByActiveCheckIn?: boolean;
  activeCheckInTooltip?: string;
};

export const WOActionButtons = ({
  ticketId,
  currentStatus,
  role,
  dispatch,
  onOpenDelayModal,
  onOpenRejectModal,
  onOpenSummaryModal,
  allTasksDone = true,
  blockedByActiveCheckIn = false,
  activeCheckInTooltip,
}: WOActionButtonsProps) => {
  if (currentStatus === "DONE") {
    return (
      <Tag color="success" icon={<CheckCircleOutlined />}>
        Work Order Completed
      </Tag>
    );
  }

  if (currentStatus === "IN_REVIEW" && role === "field") {
    return <Typography.Text type="secondary">Awaiting manager review</Typography.Text>;
  }

  const actions = WO_STATUS_TRANSITIONS[currentStatus]?.[role] ?? [];

  if (actions.length === 0) return null;

  // Field (mobile) renders a full-width row with 44px touch targets that split
  // the width evenly; management keeps natural-width inline buttons on desktop.
  const isFieldRow = role === "field";

  const handleAction = (action: WOAction["action"]) => {
    switch (action) {
      case "start":
        dispatch(ticketActions.startWorkOrder(ticketId));
        break;
      case "review":
        dispatch(ticketActions.reviewWorkOrder(ticketId));
        break;
      case "delay":
        onOpenDelayModal();
        break;
      case "done":
        onOpenSummaryModal?.();
        break;
    }
  };

  const buttons = actions.map((actionConfig) => {
    if (actionConfig.action === "confirm") {
      return (
        <Tooltip
          key="confirm"
          title={blockedByActiveCheckIn ? activeCheckInTooltip : undefined}
        >
          <Popconfirm
            title="Confirm this Work Order as Done?"
            description="This will move it to Done status."
            okText="Confirm"
            cancelText="Cancel"
            disabled={blockedByActiveCheckIn}
            onConfirm={() => dispatch(ticketActions.confirmWorkOrder(ticketId))}
          >
            <Button type="primary" block={isFieldRow} disabled={blockedByActiveCheckIn}>
              {actionConfig.label}
            </Button>
          </Popconfirm>
        </Tooltip>
      );
    }

    if (actionConfig.action === "reject") {
      return (
        <Button key="reject" danger block={isFieldRow} onClick={() => onOpenRejectModal?.()}>
          {actionConfig.label}
        </Button>
      );
    }

    // Frontend-only guards — the backend /end/ endpoint does not currently validate
    // task completion or open check-in sessions server-side. A direct API call could
    // bypass these; they are UX assistance, not enforcement.
    const isCompleteBlockedByTasks =
      actionConfig.action === "done" && role === "field" && !allTasksDone;
    const isCompleteBlockedByCheckIn =
      actionConfig.action === "done" && role === "field" && blockedByActiveCheckIn;
    const isCompleteBlocked = isCompleteBlockedByTasks || isCompleteBlockedByCheckIn;

    // Incomplete tasks take precedence over an active check-in in the tooltip: a
    // technician resolves tasks first, then checks out.
    const completeBlockedTooltip = isCompleteBlockedByTasks
      ? "All tasks must be marked as done before completing this Work Order."
      : isCompleteBlockedByCheckIn
        ? activeCheckInTooltip
        : undefined;

    return (
      <Tooltip key={actionConfig.action} title={completeBlockedTooltip}>
        <Button
          type={actionConfig.buttonType}
          onClick={() => handleAction(actionConfig.action)}
          disabled={isCompleteBlocked}
          block={isFieldRow}
        >
          {actionConfig.label}
        </Button>
      </Tooltip>
    );
  });

  if (isFieldRow) {
    return (
      <ConfigProvider theme={{ token: { controlHeight: 44 } }}>
        <Flex gap={8} className={styles.fieldRow}>
          {buttons}
        </Flex>
      </ConfigProvider>
    );
  }

  return <Space wrap>{buttons}</Space>;
};
